import { cn } from '../../lib/utils';

/**
 * Inline spinner.
 *
 * Decorative by default (`aria-hidden`), because most callers already expose the
 * busy state themselves (e.g. a button with `aria-busy`). Pass a `label` — always
 * an already-translated string — when the spinner *is* the status, and it becomes
 * a live region instead.
 */
export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <svg
      className={cn('h-5 w-5 animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  );
}

/**
 * Full-viewport loading state used while the session is being resolved.
 * The label is supplied by the caller (already translated).
 */
export function FullPageLoader({ label }: { label: string }) {
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-muted"
      role="status"
      aria-live="polite"
    >
      <Spinner className="h-7 w-7 text-brand" label={label} />
      <p className="text-sm">{label}</p>
    </div>
  );
}
